import React from "react";
import { Bar } from "react-chartjs-2";
import { Box, useTheme } from "@mui/material";
import { adjustColorRange, GraphColors } from "./helper";

const dataLabelsPlugin = {
  id: "dataLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.font = "bold 20px Roboto";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (datasetIndex === 0) {
        const meta = chart.getDatasetMeta(datasetIndex);
        meta.data.forEach((bar, index) => {
          const value = Math.abs(dataset.revenue[index]);

          const x = bar.base;
          const y = bar.y;
          ctx.fillText(
            value.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            }),
            x,
            y,
          );
        });
      }
    });
    ctx.restore();
  },
};

const FunnelChart = ({
  labels = [],
  values = [],
  datasets = null,
  sx,
  options = {},
  xLabel = "",
  yLabel = "",
}) => {
  const { palette } = useTheme();
  let colors = adjustColorRange(GraphColors[7], 0, 8);

  const generateData = () => {
    const leftValues = values.map((v) => -Math.abs(v.value));
    const rightValues = values.map((v) => Math.abs(v.value));
    const leftPercentages = values.map((v) => -Math.abs(v.percentage));
    const rightPercentages = values.map((v) => Math.abs(v.percentage));

    return {
      labels: labels,
      datasets: [
        {
          label: "Left",
          data: leftPercentages,
          revenue: leftValues,
          backgroundColor: colors,
          hoverBackgroundColor: colors,
          borderColor: colors,
          borderWidth: 0,
          barPercentage: 1.0,
          categoryPercentage: 1.0,
          borderRadius: 0,
          maxBarThickness: 130,
        },
        {
          label: "Right",
          data: rightPercentages,
          revenue: rightValues,
          backgroundColor: colors,
          hoverBackgroundColor: colors,
          borderColor: colors,
          borderWidth: 0,
          barPercentage: 1.0,
          categoryPercentage: 1.0,
          borderRadius: 0,
          maxBarThickness: 130,
        },
      ],
    };
  };

  const defaultOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = Math.abs(context.raw);
            const revenue = Math.abs(
              context.dataset.revenue[context.dataIndex],
            );

            return `Percent: ${typeof value === "number" ? value.toLocaleString() : value}% | Revenue: ${revenue.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
          drawOnChartArea: false,
          tickLength: 0,
        },
        title: {
          display: !!xLabel,
          text: xLabel,
          color: palette.custom?.sub_heading,
          font: {
            family: "Roboto",
            size: 14,
            weight: "400",
          },
          padding: { top: 10 },
        },
        ticks: {
          color: palette.custom?.sub_heading,
          font: {
            family: "Roboto",
            size: 12,
            weight: "400",
          },
          callback: function (value) {
            return Math.abs(value).toLocaleString() + "%";
          },
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        title: {
          display: !!yLabel,
          text: yLabel,
          color: palette.custom?.sub_heading,
          font: {
            family: "Roboto",
            size: 14,
            weight: "400",
          },
          padding: { bottom: 10 },
        },
        ticks: {
          color: palette.custom?.sub_heading,
          font: {
            family: "Roboto",
            size: 12,
            weight: "400",
          },
        },
      },
    },
    ...options,
  };

  return (
    <Box sx={{ height: "100%", ...sx }}>
      <Bar
        data={generateData()}
        options={defaultOptions}
        // plugins={[dataLabelsPlugin]}
      />
    </Box>
  );
};

export default FunnelChart;
